import React, { useState, useRef, useEffect } from 'react'
import styled from '@emotion/styled'

import { useGlobalOverlay } from '../../context/GlobalOverlayContext'
import theme from '../../styles/theme'
import {
  TagSuccess,
  TagInfo,
  TagDanger,
} from './Tags'

// Styled components
const StatusContainer = styled.div<{ isInteractive?: boolean }>`
  position: relative;
  display: inline-flex;
  cursor: ${(props) => (props.isInteractive ? 'pointer' : 'default')};

  ${(props) =>
    props.isInteractive &&
    `
    &:hover {
      opacity: 0.8;
    }
  `}
`

const Tooltip = styled.div`
  background-color: white;
  border: 1px solid ${theme.colors.backgroundDark};
  border-radius: 4px;
  padding: 8px 12px;
  box-shadow: ${theme.shadows.medium};
  z-index: 2000;
  width: max-content;
  max-width: 300px;
  margin-top: 5px;
  transform: translateY(-10px);
  font-size: 12px;
  color: ${theme.colors.standardText};
  line-height: 1.4;
  white-space: pre-wrap;
`

// Types
export type UploadStatus = 'new' | 'update' | 'error'

interface StatusTagWithTooltipProps {
  status: UploadStatus
  isNew: boolean // Whether the dataset is new or an update (used for error tooltip context)
  currentObservations?: number | null
  importObservations?: number | null
  errors?: string[] | null
}

const STATUS_MAPPING: Record<UploadStatus, { tag: React.JSX.Element; label: string }> = {
  new: { tag: <TagSuccess label="New" />, label: 'New' },
  update: { tag: <TagInfo label="Update" />, label: 'Update' },
  error: { tag: <TagDanger label="Error" />, label: 'Error' },
}

const StatusTagWithTooltip: React.FC<StatusTagWithTooltipProps> = ({
  status,
  isNew,
  currentObservations,
  importObservations,
  errors,
}) => {
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { addOverlay, removeOverlay } = useGlobalOverlay()

  // Build tooltip content based on status
  const tooltipContent = React.useMemo(() => {
    const datasetType = isNew ? 'New' : 'Update'
    const lines: string[] = [`Dataset: ${datasetType}`]

    // Show current observations for updates
    if (!isNew && currentObservations !== undefined && currentObservations !== null) {
      lines.push(`Current observations: ${currentObservations}`)
    }

    // Show observations in import
    lines.push(`Observations in import: ${importObservations ?? '-'}`)

    // Show errors
    if (errors && errors.length > 0) {
      lines.push('Errors:')
      errors.forEach((error) => {
        lines.push(`- ${error}`)
      })
    } else {
      lines.push('Errors: No errors')
    }

    return lines.join('\n')
  }, [isNew, currentObservations, importObservations, errors])

  const tooltipKey = `status-tooltip-${status}-${importObservations ?? 0}`

  // Clean up the overlay when component unmounts or when tooltip is hidden
  useEffect(() => {
    return () => {
      removeOverlay(tooltipKey)
    }
  }, [removeOverlay, tooltipKey])

  // Handle tooltip visibility - always show tooltip for all statuses
  useEffect(() => {
    if (showTooltip) {
      const tooltipElement = <Tooltip>{tooltipContent}</Tooltip>

      addOverlay(tooltipKey, tooltipElement, containerRef.current, 'bottom')
    } else {
      removeOverlay(tooltipKey)
    }
  }, [
    showTooltip,
    tooltipContent,
    addOverlay,
    removeOverlay,
    tooltipKey,
  ])

  return (
    <StatusContainer
      ref={containerRef}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      isInteractive={true}
    >
      {STATUS_MAPPING[status]?.tag || <TagDanger label="unknown" />}
    </StatusContainer>
  )
}

export default StatusTagWithTooltip
